import { useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useLoad, Card, Stat, Badge, Sim, H1, ainput } from "../../components/admin/ui";
import { money, pct, dt } from "../../lib/api";

function Filters({ f, setF, brands, sups }) {
  const sel = (k, opts, testid) => (
    <select data-testid={testid} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={ainput}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-8" data-testid="dashboard-filters">
      {sel("period", [[7, "7 jours"], [30, "30 jours"], [90, "90 jours"]], "filter-period")}
      {sel("brand", [["", "Toutes marques"], ...(brands || []).map((b) => [b.id, b.name])], "filter-brand")}
      {sel("market", [["", "Tous"], ["CA", "CA"]], "filter-market")}
      {sel("supplier", [["", "Tous fournisseurs"], ...(sups || []).map((s) => [s.id, s.name])], "filter-supplier")}
      {sel("channel", [["", "Tous canaux"], ["google", "Google"], ["meta", "Meta"], ["tiktok", "TikTok"], ["pinterest", "Pinterest"]], "filter-channel")}
    </div>
  );
}

export default function Dashboard() {
  const [f, setF] = useState({ period: 30, brand: "", market: "", supplier: "", channel: "" });
  const [d] = useLoad("/admin/dashboard", f);
  const [brands] = useLoad("/admin/brands");
  if (!d) return <div className="label-xs">Chargement…</div>;
  const k = d.kpis;
  const D = d.definitions;
  return (
    <div>
      <H1 sub="Chaque indicateur affiche sa définition au survol. Les montants publicitaires proviennent de connecteurs SIMULÉS.">Tableau de bord</H1>
      <Filters f={f} setF={setF} brands={brands} sups={d.suppliers} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat testid="kpi-revenue-ht" label="CA hors taxes" value={money(k.revenue_ht)} sub={`${k.orders} commandes`} />
        <Stat testid="kpi-cogs" label="Coût des produits" value={money(k.cogs)} />
        <Stat testid="kpi-payment-fees" label="Frais de paiement" value={money(k.payment_fees)} />
        <Stat testid="kpi-profit-est" label="Profit estimé" value={money(k.profit_est)} tone={k.profit_est < 0 ? "text-red-400" : "text-emerald-400"} />
        <Stat testid="kpi-profit-real" label="Profit constaté" value={money(k.profit_realized)} sub={`${k.profit_realized_orders} commande(s) au coût réel`} />
        <Stat testid="kpi-avg-margin" label="Marge moyenne" value={pct(k.avg_margin)} />
        <Stat testid="kpi-action-needed" label="Commandes à traiter" value={k.orders_action_needed} tone={k.orders_action_needed ? "text-amber-400" : ""} />
        <Stat testid="kpi-returns-open" label="Demandes de retour" value={k.returns_open} sub={`rupture : ${k.out_of_stock}`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat testid="kpi-revenue" label="Chiffre d'affaires" value={money(k.revenue)} title={D.revenue} sub={`${k.orders} commandes`} />
        <Stat testid="kpi-est-contribution" label="Contribution estimée" value={money(k.est_contribution)} title={D.est_contribution} />
        <Stat testid="kpi-realized-contribution" label="Contribution réalisée" value={money(k.realized_contribution)} title={D.realized_contribution} tone={k.realized_contribution < 0 ? "text-red-400" : "text-emerald-400"} />
        <Stat testid="kpi-margin-order" label="Marge / commande" value={money(k.margin_per_order)} title={D.margin_per_order} />
        <Stat testid="kpi-ad-spend" label="Dépenses pub" value={money(k.ad_spend)} title={D.ad_spend} />
        <Stat testid="kpi-cac" label="CAC" value={k.cac == null ? "—" : money(k.cac)} title={D.cac} />
        <Stat testid="kpi-roas" label="ROAS (SIMULÉ)" value={k.roas ?? "—"} title={D.roas} />
        <Stat testid="kpi-conversion" label="Conversion" value={pct(k.conversion_rate, 2)} title={D.conversion_rate} sub={`${k.sessions} sessions`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <Stat testid="kpi-late-orders" label="Commandes en retard" value={k.late_orders} tone={k.late_orders ? "text-amber-400" : ""} />
        <Stat testid="kpi-pending-refunds" label="Remb. en attente" value={k.pending_refunds} sub={money(k.pending_refunds_eur)} />
        <Stat testid="kpi-refunds" label="Remboursé" value={money(k.refunds_eur)} />
        <Stat testid="kpi-pub-failure" label="Échec publications" value={pct(k.publication_failure_rate)} />
        <Stat testid="kpi-security" label="Alertes sécurité" value={k.security_alerts} tone={k.security_alerts ? "text-red-400" : ""} />
        <Stat testid="kpi-cash-forecast" label="Trésorerie 30 j" value={money(k.cash_forecast_30d)} title={D.cash_forecast_30d} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card title="Chiffre d'affaires vs contribution (CAD)" className="lg:col-span-2" testid="dashboard-chart">
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={d.series}>
                <CartesianGrid stroke="#1E222D" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#111319", border: "1px solid #1E222D", fontSize: 12 }} />
                <Area dataKey="revenue" name="CA" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.12} />
                <Area dataKey="contribution" name="Contribution" stroke="#10B981" fill="#10B981" fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="État des stocks (publiés)" testid="dashboard-stock">
          {Object.entries({ in_stock: "En stock", low: "Stock faible", out: "Rupture", stale: "Données périmées" }).map(([key, l]) => (
            <div key={key} className="flex justify-between py-2 border-b border-[#1E222D] text-sm"><span>{l}</span><span className="font-mono">{d.stock[key]}</span></div>
          ))}
        </Card>
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card title="Produits les plus performants" testid="dashboard-top-products">
          {d.top_products.map((p) => <Link key={p.id} to={`/admin/products/${p.id}`} className="flex justify-between py-1.5 text-sm hover:text-emerald-300"><span className="truncate pr-3">{p.title}</span><span className="font-mono">{money(p.stats?.contribution)}</span></Link>)}
        </Card>
        <Card title="Produits non rentables" testid="dashboard-unprofitable">
          {!d.unprofitable_products.length && <div className="text-sm text-slate-500">Aucun.</div>}
          {d.unprofitable_products.map((p) => <Link key={p.id} to={`/admin/products/${p.id}`} className="flex justify-between py-1.5 text-sm hover:text-red-300"><span className="truncate pr-3">{p.title}</span><span className="font-mono text-red-400">{pct(p.pricing?.contribution_margin)}</span></Link>)}
        </Card>
        <Card title="Connecteurs" testid="dashboard-connectors">
          {d.connectors.map((c) => <div key={c.name} className="flex justify-between items-center py-1.5 text-sm"><span>{c.name}</span>{c.simulated ? <Sim /> : <Badge v={c.status} />}</div>)}
        </Card>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Santé fournisseurs" testid="dashboard-suppliers">
          {d.suppliers.map((s) => <div key={s.id} className="grid grid-cols-4 gap-2 py-1.5 text-sm"><span className="col-span-2 truncate">{s.name}</span><span className="font-mono">{pct(s.reliability, 0)} fiab.</span><span className="font-mono text-slate-500 text-xs">{dt(s.last_sync_at)}</span></div>)}
        </Card>
        <Card title="Consommation API & coûts" testid="dashboard-api-usage" right={<span className="text-xs text-slate-500">Infra : {money(k.infra_cost_eur)}</span>}>
          {d.api_usage.map((u) => <div key={u.service} className="flex justify-between py-1.5 text-sm"><span>{u.service} {u.simulated && <Sim />}</span><span className="font-mono">{u.calls} appels · {money(u.cost_eur)}</span></div>)}
        </Card>
      </div>
    </div>
  );
}
